import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { PaymentMethodTypes } from '@subscriptions/domain/types';

/**
 * Layer-neutral payment-method fields. Both backend shapes funnel through this:
 * confirm-session (CARD) and generate-reference (SPEI), which both expose the
 * canonical `providerPublic*` keys. Infrastructure mappers translate their
 * API-specific keys into this normalized shape before delegating here, so the
 * resulting {@link PaymentMethodViewDto} is identical across endpoints.
 *
 * - `providerPublicId` — CLABE for SPEI, last-four digits for CARD.
 * - `brand` / `expirationDate` / `lastFourDigits` — CARD only.
 */
export type RawPaymentMethodFields = {
  uuid: string;
  orgUuid: string;
  provider: string;
  providerId?: string | null;
  providerPublicId?: string | null;
  methodType: string;
  status: string;
  brand?: string | null;
  expirationDate?: string | null;
  lastFourDigits?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

/**
 * Builds an "MM/YY" card expiration string from the canonical providerPublic*
 * month/year fields. Returns null unless both are present.
 */
export const buildCardExpiration = (month?: string | null, year?: string | null): string | null =>
  month && year ? `${month}/${year}` : null;

/**
 * Normalizes raw payment-method fields into the shared PaymentMethodViewDto,
 * coalescing an absent providerId to null (so `providerId === null` is reliable),
 * an absent providerPublicId to '', and absent card fields to null.
 *
 * The card-only fields (`brand` / `expirationDate` / `lastFourDigits`) are forced
 * to null for non-CARD methods. SPEI exposes its CLABE through `providerPublicId`
 * (which both API shapes also map into `lastFourDigits`), so this guard keeps the
 * CLABE out of `lastFourDigits` and lets callers pass the canonical `providerPublic*`
 * keys through without leaking it.
 *
 * @param raw - Normalized payment-method fields from either API shape
 * @returns Consistent view DTO consumed by confirmation screens + detail/list cards
 */
export const mapToPaymentMethodViewDto = (raw: RawPaymentMethodFields): PaymentMethodViewDto => {
  const isCard = raw.methodType === PaymentMethodTypes.CARD;

  return {
    uuid: raw.uuid,
    orgUuid: raw.orgUuid,
    provider: raw.provider,
    providerId: raw.providerId ?? null,
    providerPublicId: raw.providerPublicId ?? '',
    methodType: raw.methodType,
    brand: isCard ? raw.brand ?? null : null,
    expirationDate: isCard ? raw.expirationDate ?? null : null,
    lastFourDigits: isCard ? raw.lastFourDigits ?? null : null,
    status: raw.status,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
};
